import React, { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Box, Typography, Container, CircularProgress } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import MenteeHeader from "../../components/MenteeHeader";
import BackButton from "../../components/BackButton";
import DashboardFooter from "../../components/Footer";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Defined at module scope so its object identity is stable across renders.
// Passing a fresh inline `options` object to <Document> makes react-pdf treat the
// input as changed and reload — destroying the running pdfjs worker mid-render,
// which surfaces as "Cannot read properties of null (reading 'sendWithPromise')"
// from the page layers. A stable reference prevents the reload race.
const PDF_OPTIONS = {
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
};

// ~US Letter height/width, used to reserve each page's box height so the spinner
// overlay has somewhere to sit before the real canvas dimensions are known.
const DEFAULT_PAGE_RATIO = 1.294;

// Renders a <Page> with a spinner overlaid on its area until the canvas finishes
// painting. For large PDFs the per-page render is slow, and react-pdf's own loading
// indicator only covers page *load*, not *render* — so without this the box just sits
// blank white while it decodes.
const PageWithSpinner = ({ pageNumber, width, thumbnail = false }) => {
  const [rendered, setRendered] = useState(false);
  const done = () => setRendered(true);
  return (
    <Box
      sx={{
        position: "relative",
        width: `${width}px`,
        // Reserve a height for the spinner before render; once the real page is in,
        // drop it so the box hugs the actual page (landscape slides are short/wide).
        minHeight: rendered ? 0 : `${Math.round(width * DEFAULT_PAGE_RATIO)}px`,
      }}
    >
      {!rendered && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
          }}
        >
          <CircularProgress size={thumbnail ? 20 : 40} />
        </Box>
      )}
      <Page
        pageNumber={pageNumber}
        width={width}
        loading={null}
        onRenderSuccess={done}
        onRenderError={done}
      />
    </Box>
  );
};

const PreviewPdf = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const file = location.state?.resourceFileUrl;
  const eventTitle = location.state?.title;
  const [numPages, setNumPages] = useState(null);
  const [currentPageInView, setCurrentPageInView] = useState(1);

  const pageRefs = useRef([]);
  const thumbnailRefs = useRef([]);
  const scrollContainerRef = useRef(null); // Ref for the main scrolling area

  const mainPageWidth = Math.min(window.innerWidth * 0.65, 800);

  // This function runs when the PDF document is loaded.
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    // Create an array of refs, one for each page and thumbnail
    pageRefs.current = Array(numPages)
      .fill(null)
      .map(() => React.createRef());
    thumbnailRefs.current = Array(numPages)
      .fill(null)
      .map(() => React.createRef());
  };

  // EFFECT 1: Set up a debounced scroll listener to detect which page is visible
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || numPages === null) return;

    // --- Debounce function ---
    const debounce = (func, delay) => {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          func.apply(this, args);
        }, delay);
      };
    };

    // --- The logic to find the current page ---
    const findCurrentPage = () => {
      let bestMatch = { page: 1, distance: Infinity };
      const containerTop = scrollContainer.scrollTop;
      const containerMidpoint = containerTop + scrollContainer.clientHeight / 2;

      pageRefs.current.forEach((ref, index) => {
        if (ref.current) {
          const pageElement = ref.current;
          const pageMidpoint =
            pageElement.offsetTop + pageElement.offsetHeight / 2;
          const distance = Math.abs(containerMidpoint - pageMidpoint);

          if (distance < bestMatch.distance) {
            bestMatch = { page: index + 1, distance: distance };
          }
        }
      });

      // Only update state if the page has actually changed
      if (bestMatch.page !== currentPageInView) {
        setCurrentPageInView(bestMatch.page);
      }
    };

    const handleScroll = debounce(findCurrentPage, 150); // Debounce for 150ms

    scrollContainer.addEventListener("scroll", handleScroll);

    // Cleanup function
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [numPages, currentPageInView]); // Re-attach if numPages changes

  useEffect(() => {
    const updatePdfLinks = () => {
      const links = document.querySelectorAll(".annotationLayer a");
      links.forEach((link) => {
        const href = link.getAttribute("href");

        // Only modify real external URLs (http/https)
        if (href && /^https?:\/\//i.test(href)) {
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener noreferrer");
        } else {
          // Keep internal PDF anchors (#page=3 etc.) working normally
          link.removeAttribute("target");
          link.removeAttribute("rel");
        }
      });
    };

    // Initial run
    updatePdfLinks();

    // Observe changes since react-pdf renders async
    const observer = new MutationObserver(updatePdfLinks);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [numPages]);

  // EFFECT 2: Scroll the active thumbnail into view whenever currentPageInView changes
  useEffect(() => {
    const activeThumbnail =
      thumbnailRefs.current[currentPageInView - 1]?.current;
    if (activeThumbnail) {
      activeThumbnail.scrollIntoView({
        behavior: "smooth",
        block: "center", // Scrolls the thumbnail to the center of its container
      });
    }
  }, [currentPageInView]); // This effect runs only when the active page changes

  return (
    <Box>
      <MenteeHeader />
      <Box className="previewpdf">
        <Container maxWidth="xl">
          <Box
            sx={{
              padding: "10px",
              mb: "10px",
              display: "flex",
              justifyContent: {
                sm: "center",
                xs: "flex-start",
                md: "center",
              },
              alignItems: "center",
            }}
          >
            <BackButton
              leftNormal={true}
              margin={0}
              onBackStep={() => navigate(-1)}
            />
            <Typography
              variant="h5"
              sx={{
                color: "#2A4876",
                fontWeight: "bold",
                marginLeft: { xs: "30px", md: 0 },
              }}
            >
              {decodeURIComponent(eventTitle || "")}
            </Typography>
          </Box>

          {/* A single <Document> parses the file once; both the thumbnail rail and
              the main page list draw from it. */}
          <Document
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            options={PDF_OPTIONS}
            loading={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  p: 6,
                  minHeight: "40vh",
                }}
              >
                <CircularProgress />
                <Typography sx={{ color: "#2A4876" }}>
                  Loading PDF… large files may take a little while.
                </Typography>
              </Box>
            }
            error={
              <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                <Typography color="error">
                  Couldn’t load this PDF. Please try again.
                </Typography>
              </Box>
            }
          >
            <Box
              className="pdfsidebar"
              sx={{
                display: "flex",
                height: "calc(100vh - 200px)",
                border: "1px solid #e0e0e0",
              }}
            >
              {/* Left Sidebar - Thumbnails */}
              {window.innerWidth > 768 && (
                <Box
                  sx={{
                    width: "20%",
                    minWidth: "200px",
                    overflowY: "auto",
                    borderRight: "1px solid #ccc",
                    backgroundColor: "#f5f5f5",
                    p: 1,
                  }}
                >
                  {Array.from(new Array(numPages), (_, index) => (
                    <Box
                      className="pdf-thumbnail"
                      key={`thumb-${index}`}
                      ref={thumbnailRefs.current[index]}
                      sx={{
                        mb: 1,
                        border:
                          currentPageInView === index + 1
                            ? "3px solid #1976d2"
                            : "1px solid #ddd",
                        cursor: "pointer",
                        borderRadius: "4px",
                        transition: "border 0.2s ease-in-out",
                        "&:hover": { borderColor: "#2A4876" },
                      }}
                      onClick={() => {
                        pageRefs.current[index]?.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }}
                    >
                      <PageWithSpinner
                        pageNumber={index + 1}
                        width={180}
                        thumbnail
                      />
                    </Box>
                  ))}
                </Box>
              )}
              {/* Right Scrollable Pages */}
              <Box
                ref={scrollContainerRef}
                id="pdf-scroll-container"
                sx={{
                  flexGrow: 1,
                  p: 2,
                  overflowY: "auto",
                  overflowX: "hidden",
                  backgroundColor: "#eef2f6",
                }}
              >
                {Array.from(new Array(numPages), (_, index) => (
                  <div
                    key={`page-${index}`}
                    ref={pageRefs.current[index]}
                    style={{
                      marginBottom: "20px",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <PageWithSpinner
                      pageNumber={index + 1}
                      width={mainPageWidth}
                    />
                  </div>
                ))}
              </Box>
            </Box>
          </Document>
        </Container>
      </Box>
      <DashboardFooter />
    </Box>
  );
};

export default PreviewPdf;
